/**
 * Risk & Compliance admin pages.
 *
 * Three lightweight management screens:
 *   - RiskScoringList: compute/display risk scores for counterparties and products
 *   - KYCList: KYC records CRUD (lightweight)
 *   - AlertsList: ack-able compliance alerts
 *
 * Each page consumes /api/risk/* and reuses MasterCounterpartyPicker where applicable.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, AlertTriangle, CheckCircle, Loader2, Play, Bell, User, Package,
  Flame, ShieldCheck, BellOff,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import MasterCounterpartyPicker from '../../../components/admin/MasterCounterpartyPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const useAuthHeaders = () => useMemo(() => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
}), []);

// ============================================================ Risk Scoring
export const RiskScoringList = () => {
  const { toast } = useToast();
  const headers = useAuthHeaders();
  const [subject, setSubject] = useState('counterparty');  // 'counterparty' | 'product'
  const [targetId, setTargetId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!targetId) { toast({ title: 'Pick a subject first', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      const path = subject === 'counterparty' ? `score/counterparty/${targetId}` : `score/product/${targetId}`;
      const r = await axios.post(`${API_URL}/api/risk/${path}`, {}, { headers });
      setResult(r.data);
      toast({ title: `Score: ${r.data.score}`, description: `Level: ${r.data.level}` });
    } catch (err) {
      toast({ title: 'Scoring failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const [screeningHits, setScreeningHits] = useState([]);
  const screen = async () => {
    if (subject !== 'counterparty' || !targetId) return;
    try {
      const r = await axios.post(`${API_URL}/api/risk/sanctions/screen/${targetId}`, {}, { headers });
      setScreeningHits(r.data || []);
      toast({ title: r.data.length ? `${r.data.length} sanctions hit(s)` : 'No sanctions match' });
    } catch (err) { toast({ title: 'Screen failed', variant: 'destructive' }); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="risk-scoring-page">
      <BackButton to="/admin/command-center" label="Command Center" />
      <h1 className="text-2xl font-bold text-[#2B2D42] mb-6 flex items-center gap-2 mt-4">
        <AlertTriangle className="w-5 h-5 text-[#FF6B35]" /> Risk Scoring
      </h1>

      <Card className="mb-5">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <select className="px-2 py-2 border rounded text-sm" value={subject}
                    onChange={(e) => { setSubject(e.target.value); setTargetId(''); setResult(null); }}
                    data-testid="risk-subject-select">
              <option value="counterparty">Counterparty</option>
              <option value="product">Product</option>
            </select>
            {subject === 'counterparty' ? (
              <div className="flex-1"><MasterCounterpartyPicker value={targetId || null} onSelect={(cp) => setTargetId(cp?.id || '')} /></div>
            ) : (
              <input placeholder="Product UUID"
                     className="flex-1 px-3 py-2 border rounded text-sm font-mono"
                     value={targetId} onChange={(e) => setTargetId(e.target.value)}
                     data-testid="risk-product-id-input" />
            )}
            <Button onClick={run} disabled={loading} className="bg-[#023E8A] text-white" data-testid="risk-score-run-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Score
            </Button>
            {subject === 'counterparty' && (
              <Button onClick={screen} variant="outline" data-testid="risk-screen-btn">
                <ShieldCheck className="w-4 h-4 mr-1" /> Screen Sanctions
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="mb-5">
          <CardContent className="p-5" data-testid="risk-score-result">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-[#2B2D42]">Score: {result.score}</h3>
                <Badge className={LEVEL_STYLES[result.level]}>{result.level}</Badge>
              </div>
            </div>
            <h4 className="text-xs font-bold text-[#6C757D] uppercase mb-2">Risk Factors</h4>
            <div className="space-y-1">
              {(result.factors || []).map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2 border rounded text-sm">
                  <span className="font-medium text-[#2B2D42]">{f.label}</span>
                  <Badge variant="outline">+{f.points} pts</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {screeningHits.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#2B2D42] mb-3 flex items-center gap-2">
              <Flame className="w-4 h-4 text-red-500" /> Sanctions Hits ({screeningHits.length})
            </h3>
            {screeningHits.map((h) => (
              <div key={h.id} className="p-2 border border-red-200 bg-red-50 rounded text-sm mb-2">
                <div className="font-semibold text-red-900">{h.matched_name} ({h.list_name.toUpperCase()})</div>
                <div className="text-xs text-red-700">Match score: {h.match_score}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// ============================================================ KYC
export const KYCList = () => {
  const { toast } = useToast();
  const headers = useAuthHeaders();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/risk/kyc`, { headers });
      setRows(r.data || []);
    } catch (_) { setRows([]); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/risk/kyc/${id}`, { status: 'approved' }, { headers });
      toast({ title: 'KYC approved' }); load();
    } catch (_) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="kyc-list-page">
      <BackButton to="/admin/command-center" label="Command Center" />
      <div className="flex items-center justify-between mb-6 mt-4">
        <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#023E8A]" /> KYC / KYB Records
        </h1>
        <Button onClick={() => setShowCreate(true)} className="bg-[#023E8A] text-white" data-testid="kyc-create-btn">
          + New KYC
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-[#6C757D]"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-[#6C757D] text-sm">No KYC records yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Counterparty</th>
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Risk Rating</th>
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Expires</th>
                  <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`kyc-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.counterparty_id.slice(0, 16)}…</td>
                    <td className="px-4 py-3">
                      <Badge className={r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'expired' || r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3"><Badge className={LEVEL_STYLES[r.risk_rating]}>{r.risk_rating}</Badge></td>
                    <td className="px-4 py-3 text-[#6C757D]">{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {r.status !== 'approved' && (
                        <Button size="sm" onClick={() => approve(r.id)} className="bg-emerald-600 text-white"
                                data-testid={`kyc-approve-${r.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" />Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showCreate && <CreateKYCModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
};


const CreateKYCModal = ({ onClose, onCreated }) => {
  const { toast } = useToast();
  const [cpId, setCpId] = useState('');
  const [risk, setRisk] = useState('medium');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!cpId) { toast({ title: 'Pick a counterparty', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const t = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/api/risk/kyc`, {
        counterparty_id: cpId, risk_rating: risk, status: 'pending',
      }, { headers: { Authorization: `Bearer ${t}` } });
      onCreated();
    } catch (err) { toast({ title: 'Create failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-[#2B2D42] mb-3">New KYC</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#6C757D] uppercase">Counterparty</label>
            <MasterCounterpartyPicker value={cpId || null} onSelect={(cp) => setCpId(cp?.id || '')} />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6C757D] uppercase">Risk Rating</label>
            <select className="mt-1 w-full px-2 py-2 border rounded text-sm" value={risk} onChange={(e) => setRisk(e.target.value)}>
              <option value="low">Low</option><option value="medium">Medium</option>
              <option value="high">High</option><option value="critical">Critical</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#023E8A] text-white" data-testid="kyc-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ============================================================ Alerts
export const AlertsList = () => {
  const { toast } = useToast();
  const headers = useAuthHeaders();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/risk/alerts`, { headers });
      setRows(r.data || []);
    } catch (_) { setRows([]); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await axios.post(`${API_URL}/api/risk/alerts/${id}/ack`, {}, { headers });
      toast({ title: 'Acknowledged' }); load();
    } catch (_) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const sev = (s) => s === 'critical' ? 'bg-red-100 text-red-900'
    : s === 'error' ? 'bg-rose-100 text-rose-800'
    : s === 'warning' ? 'bg-amber-100 text-amber-800'
    : 'bg-sky-100 text-sky-800';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="alerts-list-page">
      <BackButton to="/admin/command-center" label="Command Center" />
      <h1 className="text-2xl font-bold text-[#2B2D42] mb-6 flex items-center gap-2 mt-4">
        <Bell className="w-5 h-5 text-[#FF6B35]" /> Compliance Alerts
      </h1>
      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
            : rows.length === 0 ? <p className="p-6 text-center text-sm text-[#6C757D]">No active alerts.</p>
            : (
              <div className="divide-y">
                {rows.map((a) => (
                  <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`alert-row-${a.id}`}>
                    <AlertTriangle className="w-4 h-4 text-[#FF6B35] mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-[#2B2D42]">{a.title}</h4>
                        <Badge className={sev(a.severity)}>{a.severity}</Badge>
                        <span className="text-[10px] uppercase text-[#6C757D]">{a.kind}</span>
                      </div>
                      <p className="text-xs text-[#6C757D] mt-1">{a.message}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}>
                      <BellOff className="w-3 h-3 mr-1" /> Ack
                    </Button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
};
